import { Box, Button, Card, CardContent, CardHeader, Chip, Typography } from "@mui/material";
import {
  RESELLER_APPLICATION_DOCUMENT_KINDS,
  RESELLER_APPLICATION_DOCUMENT_LABELS,
  RESELLER_APPLICATION_STATUS_LABELS,
  type ResellerApplication,
} from "@xop/dashboard-shared";
import { ClipboardCheck, Download, ExternalLink } from "lucide-react";
import type { JSX } from "react";
import { Link as RouterLink } from "react-router-dom";

import { useToast } from "@/components/ui/use-toast";
import { formatDateTime } from "@crm-src/pages/administration/resellers/applications/format";
import { downloadApplicationDocument, useResellerOnboarding } from "@crm-src/pages/administration/resellers/applications/useResellerApplications";

/**
 * "Onboarding" — how this reseller came to exist (spec E §11.2). Shown only
 * when the reseller has a `ResellerApplication` linked to it: status, who
 * decided and when, and the four documents (download). The UPLOAD of the
 * countersigned copies happens on the application page, which this card
 * links to — one place to write, two places to read.
 *
 * Renders nothing rather than an error when the BFF refuses the actor: the
 * CRM reseller page is open to `crm:view`, the applications API to
 * `crm:resellers`, and "not for you" is not a broken page.
 */
export function ResellerOnboardingCard({ resellerId }: { resellerId: number }): JSX.Element | null {
  const { toast } = useToast();
  const query = useResellerOnboarding(resellerId);
  const applications = query.data?.applications ?? [];
  if (query.isLoading || query.isError || applications.length === 0) return null;

  const download = async (a: ResellerApplication, kind: (typeof RESELLER_APPLICATION_DOCUMENT_KINDS)[number], filename: string): Promise<void> => {
    try {
      await downloadApplicationDocument(a.id, kind, filename);
    } catch {
      toast({ title: "Couldn't download the document", description: "The CRM was unreachable.", variant: "destructive" });
    }
  };

  return (
    <Card data-testid="reseller-onboarding-card" data-cy="reseller-onboarding-card">
      <CardHeader
        title={
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, fontSize: "1.125rem", fontWeight: 600 }}>
            <Box sx={{ color: "text.secondary", display: "flex" }}><ClipboardCheck size={20} /></Box>
            <span>Onboarding</span>
          </Box>
        }
      />
      <CardContent sx={{ display: "grid", gap: 2 }}>
        {applications.map((a) => (
          <Box key={a.id} sx={{ display: "grid", gap: 1.5, p: 2, bgcolor: "surfaceSoft", borderRadius: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, flexWrap: "wrap" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Chip size="small" label={RESELLER_APPLICATION_STATUS_LABELS[a.status]} color={a.status === "active" ? "success" : a.status === "rejected" ? "default" : "info"} />
                <Typography sx={{ fontSize: 14, color: "text.secondary" }}>
                  {a.decision === "created" && "Created as a new reseller"}
                  {a.decision === "linked" && "Linked to an existing reseller"}
                  {a.decision === "rejected" && "Rejected"}
                  {a.decision === null && "Awaiting review"}
                  {a.decided_by ? ` by ${a.decided_by.full_name} on ${formatDateTime(a.decided_at)}` : ""}
                </Typography>
              </Box>
              <Button
                component={RouterLink}
                to={`/crm/administration/reseller-applications/${a.id}`}
                size="small"
                variant="outlined"
                color="inherit"
                endIcon={<ExternalLink size={14} />}
                data-cy="reseller-onboarding-open"
              >
                Open application
              </Button>
            </Box>
            <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
              Registered by {a.email} on {formatDateTime(a.created_at)}
              {a.documents_submitted_at ? ` · documents submitted ${formatDateTime(a.documents_submitted_at)}` : ""}
            </Typography>
            <Box sx={{ display: "grid", gap: 0.75 }}>
              {RESELLER_APPLICATION_DOCUMENT_KINDS.map((kind) => {
                const doc = a.documents[kind];
                return (
                  <Box key={kind} sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, fontSize: 13 }}>
                    <Box sx={{ minWidth: 0 }}>
                      <Box component="span" sx={{ color: "text.primary" }}>{RESELLER_APPLICATION_DOCUMENT_LABELS[kind]}</Box>
                      <Box component="span" sx={{ ml: 1, color: doc.attached ? "text.secondary" : "text.disabled", fontStyle: doc.attached ? "normal" : "italic" }}>
                        {doc.attached ? doc.filename : "not uploaded"}
                      </Box>
                    </Box>
                    {doc.attached && (
                      <Button
                        size="small"
                        variant="text"
                        color="inherit"
                        startIcon={<Download size={14} />}
                        onClick={() => void download(a, kind, doc.filename)}
                        data-testid={`onboarding-download-${kind}`}
                        data-cy={`onboarding-download-${kind}`}
                      >
                        Download
                      </Button>
                    )}
                  </Box>
                );
              })}
            </Box>
          </Box>
        ))}
      </CardContent>
    </Card>
  );
}
